import styles from "../../assets/scss/modules/PublicListings.module.scss";
import React, {HTMLAttributes, Key, useEffect} from 'react';
import useCtrGenresProprietes from "../../hooks/useCtrGenresProprietes.ts";
import {CtrInscriptions, Maybe, OrderedPropertyType} from "../../graphql/generated/graphql-main.tsx";
import clsx from "clsx";
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {XTextField} from "../global/XTextField.tsx";
import {deleteBy, findBy} from "../../utils/array-utils.ts";
import {useImmer} from "use-immer";
import {empty} from "../../utils/utils.ts";
import useReactTexts from "../../hooks/useReactTexts.ts";
import {OpenInNew} from "@mui/icons-material";

interface Props extends HTMLAttributes<HTMLDivElement> {
    listings: CtrInscriptions[];
    onFilter: (genres: string[]) => void;
    resetFlag: number;
}

// hard-coded label & link to E&V Immobilier Commercial from 'Property Genres' dropdown
const evCommercialGenre: OrderedPropertyType = {
    key: "@commercial",
    label: "E&V Commercial",
    value: ["@commercial"],
    order: 99
}
const evCommercialLink = "https://evimmobiliercommercial.com/";

const FiltersGenre = (props: Props) => {
    const {breakpoints} = useTheme();
    const {__} = useReactTexts();
    const is_sm = useMediaQuery(breakpoints.down("sm"));
    const {$genres, filterablePropertyTypes} = useCtrGenresProprietes();
    const [selection, setSelection] = useImmer<OrderedPropertyType[]>([]);

    // extract only the genres used in the listings to avoid "no results" for some genres
    const usedGenres = new Set(props.listings.map(it => it.GENRE_PROPRIETE || "") ?? []);
    const $finalGenres = $genres.filter(it => usedGenres.has(it.GENRE_PROPRIETE!));

    // dynamically generate autocomplete options while respecting groupings of property types
    const genreOptions: OrderedPropertyType[] = [...filterablePropertyTypes($finalGenres), evCommercialGenre];
    const optByKey = (val: string) => findBy(genreOptions, "key", val);

    // given multi-select of options (each option potentially having an array of grouped types), flatten as single arr
    const flattenGenres = (options: OrderedPropertyType[]): string[] => {
        return options
            .flatMap(option => option.value ?? [])
            .filter(it => it != null && !empty(it)) as string[];
    }

    const deleteOption = (key: Maybe<string>) => {
        setSelection(draft => {
            deleteBy(draft, "key", key);
        });
    }

    const mobileTypeSelector = () => {
        return <FormControl className={clsx("w-full mobile", styles.dd_select)}>
            <InputLabel>{__("Tous les types de propriétés")}</InputLabel>
            <Select
                variant={"outlined"}
                size={"medium"}
                multiple
                MenuProps={{
                    className: styles.popper_select
                }}
                input={<OutlinedInput label={__("Tous les types de propriétés")}/>}
                value={selection.map(it => it.key)}
                onChange={event => {
                    // convert <select> string values to OrderedPropertyType objects that we can work with easily
                    const _types = ((event.target.value ?? []) as string[])
                        .map(it => optByKey(it))
                        .filter(it => it != null) as OrderedPropertyType[];

                    // check if 'E&V Commercial' item was clicked, then redirect to new tab
                    if (_types.map(it => it.key).includes(evCommercialGenre.key)) {
                        window.open(evCommercialLink, "blank");
                        return;
                    }
                    setSelection(_types);
                }}
                renderValue={(choices: string[]) => {
                    const _labels = choices.map(type => optByKey(type)?.label ?? "").join(', ');
                    return <span className={styles.dd_select_tags}>{_labels}</span>
                }}
            >
                {genreOptions.map((option) => {
                    const _selected = findBy(selection, "key", option.key) != null;
                    const _isEvComm = option.key == evCommercialGenre.key;

                    return <MenuItem
                        key={`type-${option.key}`}
                        value={option.key!}
                        className={clsx("space-x-2", {"!bg-merino": _selected})}
                    >
                        {_isEvComm ? <OpenInNew/> : <Checkbox className={"p-0 m-0"} checked={_selected}/>}
                        <span>{option.label ?? ""}</span>
                    </MenuItem>
                })}
            </Select>
        </FormControl>
    }
    const desktopTypeSelector = () => {
        return <Autocomplete<OrderedPropertyType, boolean, boolean>
            size={"small"}
            className={clsx("", styles.dd_autocomplete)}
            classes={{
                popper: styles.popper_autocomplete,
            }}
            options={genreOptions}
            disableCloseOnSelect={true}
            multiple={true}
            value={selection}
            getOptionKey={option => option.key ?? ""}
            getOptionLabel={option => option.label ?? ""}
            isOptionEqualToValue={(option, value) => option.key == value.key}
            onChange={(event, choices: Maybe<OrderedPropertyType[]>) => {
                // if 'E&V Commercial' link is clicked, do NOT select is, but instead open new tab w/ link
                if (choices?.map(it => it.key).includes(evCommercialGenre.key)) {
                    window.open(evCommercialLink, "blank");
                    return;
                }
                // normal cases: capture the user selection
                setSelection(choices ?? []);
            }}
            renderInput={params => <XTextField
                {...params}
                label={__("Tous les types de propriétés")}
            ></XTextField>}
            renderOption={(props1: HTMLAttributes<HTMLLIElement> & { key: Key }, option) => {
                const _selected = Boolean(props1["aria-selected"]);
                const _isEvComm = option.key == evCommercialGenre.key;

                return <li {...props1} key={props1.key} className={clsx(props1.className,
                    "space-x-2", {"!bg-merino": _selected})}>
                    {_isEvComm ? <OpenInNew/> : <Checkbox className={"p-0 m-0"} checked={_selected}/>}
                    <span>{option.label}</span>
                </li>
            }}
            renderTags={(list, getTagProps: AutocompleteRenderGetTagProps) => {
                const _labels = list.map(option => option.label).join(', ');
                const _props = getTagProps({index: 0});
                return <span {..._props} key={_props.key} className={styles.dd_autocomplete_tags}>
                    {_labels}
                </span>
            }}
        ></Autocomplete>
    }

    // auto-apply genres selection as it changes
    useEffect(() => {
        props.onFilter(flattenGenres(selection ?? []));
    }, [selection]);

    // when reset flag is updated, this triggers resetting this filter
    useEffect(() => {
        if (props.resetFlag == 0) return;
        setSelection([]);
    }, [props.resetFlag]);

    return <div className={clsx("w-full", props.className)}>
        {is_sm ? mobileTypeSelector() : desktopTypeSelector()}

        {/* list user selection as deletable chips */}
        {!empty(selection) && <div className={styles.filter_chips}>
            {selection.map(opt => <Chip
                key={`chip-type-${opt.key}`}
                size={"small"}
                label={opt.label}
                onDelete={event => deleteOption(opt.key!)}
            ></Chip>)}
        </div>}
    </div>
}

export default FiltersGenre;